import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';

import type { HouseholdProfileView } from '@amumin/api-client';
import { canonical, FAITHS, FOOD_RULES } from '@amumin/domain/profile';
import { t } from '@amumin/i18n';
import { Button, showToast } from '@amumin/ui';

import { api } from '../api.js';
import { LinkButton, toastError } from '../lib/ui.js';
import { appText, context, dataVersion } from '../state/session.js';
import { familyLike, householdType, houseWords } from '../state/householdType.js';
import styles from './profile.module.css';

// Kaarten op Vandaag (docs/DESIGN.md §8.P.3): de rustige uitnodiging voor gezinnen zonder profiel
// (compact, ONDER de agenda en de taken, met een gewone knop: de agenda van vandaag blijft bovenaan
// en de pagina houdt één hoofdactie; review d-ux r1)
// ("Niet nu" 30 dagen, na de tweede keer nooit meer; "Nee, bedankt" nooit meer) en één keer de kaart
// voor een andere ouder ("Baba heeft ingesteld wat bij jullie gezin past ... Klopt dit ook voor
// jou?"). De stand staat per ouder op de server (profile_invite), nooit in localStorage. Nooit een
// modal, nooit blokkerend. Alleen voor ouders.

export function ProfileCards() {
  const a = appText.value;
  const p = a.profile;
  const view = useSignal<HouseholdProfileView | null>(null);
  const busy = useSignal(false);
  const ctx = context.value;
  const role = ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
  const isParent = role === 'owner' || role === 'parent';

  useEffect(() => {
    if (!isParent) return;
    api.app.profile
      .get()
      .then((v) => (view.value = v))
      .catch(() => (view.value = null));
  }, [isParent, dataVersion.value]);

  const v = view.value;
  if (!isParent || !v) return null;

  const answer = async (action: 'not_now' | 'never' | 'seen', message?: string) => {
    busy.value = true;
    try {
      await api.app.profile.invite(action);
      view.value = { ...v, invite: { show: false, other_parent: false } };
      if (message) showToast({ message });
    } catch (e) {
      toastError(e);
    } finally {
      busy.value = false;
    }
  };

  if (v.invite.show) {
    return (
      <CompactCard
        name="profile-invite-card"
        title={familyLike.value ? p.invite.title : houseWords.value.profileTitle}
        body={
          householdType.value === 'solo'
            ? appText.value.households.soloWords.profileBody
            : p.invite.body
        }
        actions={
          <>
            <LinkButton href="/instellingen/wat-past" size="small" name="profile-invite-start">
              {p.invite.start}
            </LinkButton>
            <Button
              name="profile-invite-later"
              variant="ghost"
              busy={busy.value}
              onPress={() => void answer('not_now', p.screens.inviteLater)}
            >
              {p.invite.later}
            </Button>
            <Button
              name="profile-invite-never"
              variant="ghost"
              busy={busy.value}
              onPress={() => void answer('never', p.screens.inviteNever)}
            >
              {p.invite.never}
            </Button>
          </>
        }
      />
    );
  }

  if (v.invite.other_parent && v.consent) {
    const faithNames = p.faiths.labels as Record<string, string>;
    const foodNames = p.food.labels as Record<string, string>;
    const parts = [
      ...(v.faiths ? canonical(v.faiths, FAITHS).map((f) => faithNames[f] ?? f) : []),
      ...canonical(v.food_rules, FOOD_RULES).map((r) => foodNames[r] ?? r),
    ];
    return (
      <CompactCard
        name="profile-other-parent-card"
        // Zonder "gezin" bij huisgenoten, stel, woongroep, ouderen thuis en zorg (golf 6b live r1).
        title={familyLike.value ? p.otherParent.title : p.otherParent.titleNeutral}
        body={t(familyLike.value ? p.otherParent.body : p.otherParent.bodyNeutral, {
          name: v.consent.given_by_name ?? '',
          what: parts.length > 0 ? parts.join(', ') : p.screens.otherParentWhat,
        })}
        actions={
          <>
            <a
              class="link-button link-button-small"
              href="/instellingen/wat-past"
              data-name="profile-other-parent-view"
              onClick={() => void api.app.profile.invite('seen').catch(() => undefined)}
            >
              <span>{p.otherParent.view}</span>
            </a>
            <Button
              name="profile-other-parent-ok"
              variant="ghost"
              busy={busy.value}
              onPress={() => void answer('seen')}
            >
              {a.common.close}
            </Button>
          </>
        }
      />
    );
  }
  return null;
}

/** Eén rustige regel: klein plaatje, kop, korte tekst en gewone knoppen (geen tweede hoofdactie). */
function CompactCard({
  name,
  title,
  body,
  actions,
}: {
  name: string;
  title: string;
  body: string;
  actions: ComponentChildren;
}) {
  return (
    <section class={`${styles.compactCard} panel-quiet`} data-name={name} aria-label={title}>
      <div class={styles.compactText}>
        <p class={styles.compactTitle}>{title}</p>
        <p class={styles.compactBody}>{body}</p>
        <div class={styles.compactActions}>{actions}</div>
      </div>
    </section>
  );
}
